/**
 * SolitaireGame (#966) — full-screen overlay for single-player Klondike
 * Solitaire. The second interactive on-screen game, built natively on the
 * #974/#975 single-audio-channel model: the game is VISUAL-ONLY and has no
 * voice of its own. Berta's voice flows entirely through the conversation
 * channel (the CallStrip shows it); this component never plays audio.
 *
 * Interaction (elderly-accessible, per the product philosophy):
 *  - TAP-TO-MOVE is the primary model — tap a face-up card to select it (and
 *    the run below it); legal destinations glow; tap one to move. Tap the
 *    selected card again to cancel. No drag-and-drop (drag is in our
 *    anti-patterns for elderly hands — tremor/dry-skin break it). Drag is a
 *    deferred follow-up; tap-to-move covers every move.
 *  - Tap the stock to draw / recycle. Undo is unlimited. No timer, no score.
 *  - Selection clears IMMEDIATELY on issuing a move (the #974c instant-tap
 *    lesson) so an uncertain user can't double-fire while the WS round-trips.
 *
 * State protocol (server-driven, mirrors domino):
 *  Inbound:  {type: solitaire_start|solitaire_state, state}, {type: solitaire_end, reason}
 *  Outbound: {type: solitaire_move, move}, {type: solitaire_draw},
 *            {type: solitaire_undo}, {type: solitaire_quit}
 *
 * `state` is SolitaireGame.to_client_state() from app/services/games/solitaire.py:
 *   { tableau: [[{r,s,up}|{up:false}], ...7], foundations: {H:[{r,s,up}],D,C,S},
 *     stock_count, waste:[...], waste_top:{r,s,up}|null, won, stuck, can_undo,
 *     last_action }
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useActivityBox } from './ActivityFrame';
import { useLang, formatString } from '../i18n/LanguageContext';

const SUITS = ['H', 'D', 'C', 'S'];
const SUIT_SYMBOL = { H: '♥', D: '♦', C: '♣', S: '♠' };
const RED_SUITS = new Set(['H', 'D']);
const RANK_LABEL = { 1: 'A', 11: 'J', 12: 'Q', 13: 'K' };

const TABLE_GREEN = '#0A4D32';
const BRAND_ORANGE = '#E8845A';
const CARD_FACE = '#FBF7EE';
const CARD_BORDER = '#C9BE9A';
const CARD_BACK = '#8B5A3C';
const HIGHLIGHT_GREEN = '#7ED957';
const HIGHLIGHT_GLOW = 'rgba(126, 217, 87, 0.45)';

export function cardColour(card) {
  return card && RED_SUITS.has(card.s) ? '#B3261E' : '#1A1A1A';
}
export function rankLabel(r) {
  return RANK_LABEL[r] || String(r);
}

// ── Client-side rule mirrors (server still validates; these only drive
//    which targets glow). Kept tiny, pure, and EXPORTED so the
//    highlight logic is unit-tested — a bug here glows a target the
//    server then rejects (#966). ─────────────────────────────────────
export function foundationAccepts(foundations, card) {
  if (!card) return false;
  const pile = (foundations && foundations[card.s]) || [];
  if (pile.length === 0) return card.r === 1;
  return card.r === pile[pile.length - 1].r + 1;
}
function tableauTop(col) {
  return col && col.length ? col[col.length - 1] : null;
}
export function canPlaceOnTableau(col, card) {
  if (!card) return false;
  const top = tableauTop(col);
  if (!top) return card.r === 13; // King onto empty
  if (!top.up) return false;
  return card.r === top.r - 1 && cardColour(card) !== cardColour(top);
}


function _useStrings() {
  const ctx = useLang();
  const s = ctx && ctx.t && ctx.t.tablet && ctx.t.tablet.solitairegame;
  return {
    done: (s && s.done) || 'Done',
    doneAria: (s && s.doneAria) || 'End the game of Solitaire',
    undo: (s && s.undo) || 'Undo',
    undoAria: (s && s.undoAria) || 'Undo the last move',
    stockAria: (s && s.stockAria) || 'Draw a card from the deck',
    recycleHint: (s && s.recycleHint) || 'Tap to turn the pile over',
    emptyColumnHint: (s && s.emptyColumnHint) || 'Only a King starts an empty column',
    foundationAria: (s && s.foundationAria) || 'Foundation, {suit}',
    stuckTitle: (s && s.stuckTitle) || 'The cards aren’t falling our way',
    stuckBody: (s && s.stuckBody) || 'No moves left this hand — it’s just the luck of the cards. Shall we try a fresh deal? Just ask, or tap Done.',
    winTitle: (s && s.winTitle) || 'What a lovely game!',
    winBody: (s && s.winBody) || 'Every card is home. Beautifully played.',
    instructions: (s && s.instructions) || 'Tap a card, then tap where it should go. Tap the deck for more cards.',
  };
}


// ── Card ──────────────────────────────────────────────────────────────
function Card({ card, w, selected = false, glow = false, dim = false,
                onClick = null, ariaLabel = null }) {
  const h = w * 1.45;
  const faceDown = !card || card.up === false;
  const border = selected ? BRAND_ORANGE : (glow ? HIGHLIGHT_GREEN : CARD_BORDER);
  const common = {
    width: w, height: h, borderRadius: Math.max(6, w * 0.12),
    boxSizing: 'border-box',
    border: `${selected || glow ? 3 : 1.5}px solid ${border}`,
    cursor: onClick ? 'pointer' : 'default',
    userSelect: 'none', position: 'relative', flexShrink: 0,
    boxShadow: glow ? `0 0 10px ${HIGHLIGHT_GLOW}` : '0 1px 2px rgba(0,0,0,0.25)',
  };
  // Shared click/keyboard wiring so BOTH face-down (e.g. the stock pile)
  // and face-up cards are interactive when given an onClick. #966 fix: the
  // stock cover used to be a bare div that silently dropped onDraw, so
  // tapping the brown deck did nothing.
  const clickProps = {
    onClick: onClick || undefined,
    role: onClick ? 'button' : 'img',
    tabIndex: onClick ? 0 : -1,
    onKeyDown: onClick ? (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); }
    } : undefined,
  };
  if (faceDown) {
    return (
      <div {...clickProps} aria-label={ariaLabel || 'face-down card'} style={{
        ...common, background: CARD_BACK,
        backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.18) 0 4px, transparent 4px 8px)',
        border: `1.5px solid #5C3A22`,
      }} />
    );
  }
  const colour = cardColour(card);
  const label = `${rankLabel(card.r)}${SUIT_SYMBOL[card.s]}`;
  return (
    <div
      {...clickProps}
      aria-label={ariaLabel || label}
      style={{
        ...common, background: dim ? '#E7E0CE' : CARD_FACE,
        color: colour, opacity: dim ? 0.7 : 1,
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        padding: Math.max(2, w * 0.06), fontWeight: 700,
        fontSize: `${Math.max(11, w * 0.30)}px`, lineHeight: 1,
      }}
    >
      <span>{rankLabel(card.r)}<span style={{ fontSize: '0.85em' }}>{SUIT_SYMBOL[card.s]}</span></span>
      <span style={{ alignSelf: 'center', fontSize: `${Math.max(16, w * 0.55)}px` }}>
        {SUIT_SYMBOL[card.s]}
      </span>
      <span style={{ alignSelf: 'flex-end', transform: 'rotate(180deg)' }}>
        {rankLabel(card.r)}<span style={{ fontSize: '0.85em' }}>{SUIT_SYMBOL[card.s]}</span>
      </span>
    </div>
  );
}

function EmptySlot({ w, glow = false, onClick = null, ariaLabel = null, symbol = '' }) {
  const h = w * 1.45;
  return (
    <div
      onClick={onClick || undefined}
      role={onClick ? 'button' : 'presentation'}
      tabIndex={onClick ? 0 : -1}
      aria-label={ariaLabel || undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); }
      } : undefined}
      style={{
        width: w, height: h, borderRadius: Math.max(6, w * 0.12),
        boxSizing: 'border-box',
        border: `2px dashed ${glow ? HIGHLIGHT_GREEN : 'rgba(248,240,220,0.35)'}`,
        background: glow ? HIGHLIGHT_GLOW : 'rgba(0,0,0,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'rgba(248,240,220,0.5)', fontSize: `${w * 0.5}px`,
        cursor: onClick ? 'pointer' : 'default', flexShrink: 0,
      }}
    >{symbol}</div>
  );
}


function EndCard({ title, body, onClose, doneLabel }) {
  return (
    <div style={{
      position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10,
    }}>
      <div style={{
        background: CARD_FACE, padding: '32px 40px', borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)', textAlign: 'center', maxWidth: '90vw',
      }}>
        <h2 style={{ color: '#2A2418', margin: '0 0 8px', fontSize: 'clamp(1.4rem, 4vw, 2rem)' }}>{title}</h2>
        <p style={{ color: '#5C3A22', margin: '0 0 24px', fontSize: 'clamp(1rem, 3vw, 1.2rem)' }}>{body}</p>
        <button onClick={onClose} style={{
          background: BRAND_ORANGE, color: 'white', border: 'none', padding: '14px 32px',
          fontSize: '1.1rem', fontWeight: 600, borderRadius: 8, cursor: 'pointer',
          minHeight: 60, minWidth: 120,
        }}>{doneLabel}</button>
      </div>
    </div>
  );
}


// ── Main component ──────────────────────────────────────────────────────
export default function SolitaireGame({
  state,
  onMove,
  onDraw,
  onUndo,
  onQuit,
}) {
  const tr = _useStrings();
  const { width: boxWidth, height: boxHeight } = useActivityBox();
  // selected source: {kind:'waste'} | {kind:'tableau', col, idx} | null
  const [selected, setSelected] = useState(null);

  const tableau = state.tableau || [];
  const foundations = state.foundations || {};
  const wasteTop = state.waste_top || null;

  // Clear stale selection whenever the board changes under us.
  useEffect(() => { setSelected(null); }, [
    state.stock_count,
    (state.waste || []).length,
    JSON.stringify(state.foundations),
    JSON.stringify((state.tableau || []).map((c) => c.length)),
  ]);

  // #1089 — cards size from the ActivityFrame's MEASURED content box.
  // 7 columns + gaps must fit the width; also cap by height so a short
  // landscape doesn't push the tableau off the bottom (the game area also
  // scrolls as a last resort). Clamp for touch + readability. Fallback
  // (700) covers first paint before the ResizeObserver has measured.
  const cardW = useMemo(() => {
    const w = boxWidth || 700;
    const h = boxHeight || 700;
    const byWidth = (w - 48) / 7.6;
    const byHeight = (h - 80) / 6.0;
    return Math.max(44, Math.min(96, Math.min(byWidth, byHeight)));
  }, [boxWidth, boxHeight]);

  // The card currently selected as the move SOURCE (a run bottom), if any.
  const selectedCard = useMemo(() => {
    if (!selected) return null;
    if (selected.kind === 'waste') return wasteTop;
    if (selected.kind === 'tableau') {
      const col = tableau[selected.col] || [];
      return col[selected.idx] || null;
    }
    return null;
  }, [selected, wasteTop, tableau]);

  // Is the selected source a single card (eligible for a foundation)?
  const selectedIsSingle = useMemo(() => {
    if (!selected) return false;
    if (selected.kind === 'waste') return true;
    const col = tableau[selected.col] || [];
    return selected.idx === col.length - 1;
  }, [selected, tableau]);

  const issue = useCallback((move) => {
    onMove(move);
    setSelected(null); // #974c — instant feedback, no double-fire
  }, [onMove]);

  const selectTableau = useCallback((col, idx) => {
    const card = (tableau[col] || [])[idx];
    if (!card || card.up === false) return;
    setSelected((prev) =>
      prev && prev.kind === 'tableau' && prev.col === col && prev.idx === idx
        ? null : { kind: 'tableau', col, idx });
  }, [tableau]);

  const selectWaste = useCallback(() => {
    if (!wasteTop) return;
    setSelected((prev) => (prev && prev.kind === 'waste') ? null : { kind: 'waste' });
  }, [wasteTop]);

  const onFoundationTap = useCallback((suit) => {
    if (!selected || !selectedIsSingle) return;
    if (!foundationAccepts(foundations, selectedCard) || selectedCard.s !== suit) return;
    if (selected.kind === 'waste') issue({ action: 'waste_to_foundation' });
    else issue({ action: 'tableau_to_foundation', col: selected.col });
  }, [selected, selectedIsSingle, selectedCard, foundations, issue]);

  const onColumnTap = useCallback((dst) => {
    if (!selected || !canPlaceOnTableau(tableau[dst], selectedCard)) return;
    if (selected.kind === 'waste') issue({ action: 'waste_to_tableau', col: dst });
    else if (selected.kind === 'tableau') {
      if (selected.col === dst) return;
      const col = tableau[selected.col] || [];
      issue({ action: 'tableau_to_tableau', src: selected.col, dst, count: col.length - selected.idx });
    }
  }, [selected, selectedCard, tableau, issue]);

  // Which targets glow given the current selection.
  const foundationGlows = (suit) =>
    !!(selected && selectedIsSingle && selectedCard && selectedCard.s === suit
       && foundationAccepts(foundations, selectedCard));
  const columnGlows = (dst) =>
    !!(selected && selectedCard && !(selected.kind === 'tableau' && selected.col === dst)
       && canPlaceOnTableau(tableau[dst], selectedCard));

  const gap = Math.max(4, cardW * 0.12);
  const stackOffset = Math.max(16, cardW * 0.30);

  const gameArea = (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0,
      padding: '10px 12px', gap, paddingBottom: 'env(safe-area-inset-bottom)',
      overflow: 'auto',
    }}>
      {/* Controls — their OWN row so they're ALWAYS visible and never cut
          off the edge on a narrow screen (Bas's 2026-06-07 staging repro).
          Done is rightmost (the primary exit), Undo to its left. The row
          wraps if it ever can't fit. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
                    gap: 8, flexWrap: 'wrap' }}>
        <button onClick={onUndo} disabled={!state.can_undo} aria-label={tr.undoAria}
                style={btnStyle(state.can_undo)}>{tr.undo}</button>
      </div>

      {/* Piles: stock + waste (left), foundations (right). Wraps so the
          foundations drop below the stock rather than overflowing. */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                    gap, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap }}>
          {/* Stock */}
          {state.stock_count > 0 ? (
            <Card card={null} w={cardW} onClick={onDraw} ariaLabel={tr.stockAria} />
          ) : (
            <EmptySlot w={cardW} onClick={onDraw} ariaLabel={tr.recycleHint} symbol={'↻'} />
          )}
          {/* Waste */}
          {wasteTop ? (
            <Card card={wasteTop} w={cardW}
                  selected={!!(selected && selected.kind === 'waste')}
                  onClick={selectWaste} />
          ) : (
            <EmptySlot w={cardW} />
          )}
        </div>
        <div style={{ display: 'flex', gap, alignItems: 'flex-start' }}>
          {SUITS.map((suit) => {
            const pile = foundations[suit] || [];
            const top = pile[pile.length - 1] || null;
            const glow = foundationGlows(suit);
            return top ? (
              <Card key={suit} card={top} w={cardW} glow={glow}
                    onClick={glow ? () => onFoundationTap(suit) : null}
                    ariaLabel={formatString(tr.foundationAria, { suit: SUIT_SYMBOL[suit] })} />
            ) : (
              <EmptySlot key={suit} w={cardW} glow={glow} symbol={SUIT_SYMBOL[suit]}
                         onClick={glow ? () => onFoundationTap(suit) : null}
                         ariaLabel={formatString(tr.foundationAria, { suit: SUIT_SYMBOL[suit] })} />
            );
          })}
        </div>
      </div>

      {/* Tableau: 7 columns */}
      <div style={{ display: 'flex', gap, justifyContent: 'center', flex: 1, alignItems: 'flex-start' }}>
        {tableau.map((col, ci) => {
          const glow = columnGlows(ci);
          return (
            <div key={ci} style={{ position: 'relative', width: cardW, flexShrink: 0 }}>
              {col.length === 0 ? (
                <EmptySlot w={cardW} glow={glow}
                           onClick={glow ? () => onColumnTap(ci) : null}
                           ariaLabel={tr.emptyColumnHint} />
              ) : (
                <div style={{ position: 'relative', height: (col.length - 1) * stackOffset + cardW * 1.45 }}>
                  {col.map((card, idx) => {
                    const isLast = idx === col.length - 1;
                    const sel = !!(selected && selected.kind === 'tableau'
                                   && selected.col === ci && idx >= selected.idx);
                    // The bottom-most face-up card of the column also acts as
                    // the column drop-target when something is selected.
                    const clickable = card.up !== false;
                    return (
                      <div key={idx} style={{ position: 'absolute', top: idx * stackOffset, left: 0 }}>
                        <Card card={card} w={cardW} selected={sel}
                              glow={isLast && glow}
                              onClick={
                                isLast && glow ? () => onColumnTap(ci)
                                : clickable ? () => selectTableau(ci, idx)
                                : null}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Gentle one-line hint — low cognitive load, always visible. */}
      <div style={{
        textAlign: 'center', color: 'rgba(248,240,220,0.6)',
        fontSize: '0.95rem', padding: '2px 0 4px',
      }}>
        {tr.instructions}
      </div>
    </div>
  );

  return (
    <div style={{
      // #1089 — fills the ActivityFrame content slot. Was fixed/inset with
      // a viewport-mode flex branch for the embedded CallStrip.
      flex: 1,
      minWidth: 0,
      minHeight: 0,
      position: 'relative',
      background: TABLE_GREEN,
      backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.04) 4px, rgba(0,0,0,0.04) 8px)',
      display: 'flex',
      flexDirection: 'column',
      paddingTop: 'env(safe-area-inset-top)',
    }}>
      {gameArea}

      {state.won && (
        <EndCard title={tr.winTitle} body={tr.winBody} onClose={onQuit} doneLabel={tr.done} />
      )}
      {!state.won && state.stuck && (
        <EndCard title={tr.stuckTitle} body={tr.stuckBody} onClose={onQuit} doneLabel={tr.done} />
      )}
    </div>
  );
}

function btnStyle(enabled) {
  return {
    background: enabled ? BRAND_ORANGE : 'rgba(0,0,0,0.25)',
    color: '#F8F0DC', border: 'none', padding: '10px 18px', borderRadius: 8,
    fontSize: '1rem', fontWeight: 600, cursor: enabled ? 'pointer' : 'not-allowed',
    minHeight: 56, minWidth: 96, opacity: enabled ? 1 : 0.5,
  };
}
